/**
 * Question Card Component
 * 
 * Minimal selection UI with staggered fade-in animation.
 * Supports multi-question flows with smooth transitions.
 */

import { memo, useState, useEffect, forwardRef, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import type {
  ChatEventByomConfiguration,
  ChatEventQuestionNodeRef,
} from '../../hooks/useChatSocket'
import type { ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import { InlineMentionComposer, type InlineMentionComposerHandle } from './InlineMentionComposer'
import { createNadDebug } from '../../utils/debugLog'
import { MentionMarkupText } from './MentionMarkup'

const questionCardLog = createNadDebug('dw:chat:question-card')

/**
 * The focusable freeform-answer field inside a question card.
 *
 * Two shapes exist: the plain `<input type="text">` on canvas-less surfaces and
 * the contentEditable editor `InlineMentionComposer` renders when `canvasNodes`
 * is supplied (post 1585245219899442). Every focus hop must match BOTH — a
 * lookup for `input[type="text"]` alone silently no-ops on mention-capable
 * cards, costing pointer users, keyboard users, and the multi-question
 * "advance to the next card" flow their focus affordance.
 */
export const ANSWER_FIELD_SELECTOR =
  'input[type="text"], [data-testid="chat-composer-editor"]'

export interface QuestionOption {
  label: string
  value?: string
  description?: string
  icon?: string
}

export interface QuestionCardProps {
  question: string
  options: QuestionOption[]
  /** Optional clip/panel identity descriptor for a passive "Clip N · vN" chip. */
  nodeRef?: ChatEventQuestionNodeRef
  /** Exact credential-free BYOM proposal rendered as an inline review sheet. */
  byomConfiguration?: ChatEventByomConfiguration
  selectedOption?: string
  onSelect: (option: string) => void
  disabled?: boolean
  allowCustomInput?: boolean
  customText?: string
  /** Whether the freeform answer is the selected radio option. */
  customSelected?: boolean
  onCustomTextChange?: (text: string) => void
  onCustomInputFocus?: () => void
  onCustomSubmit?: () => void
  autoSubmit?: boolean
  /** For multi-question flows: callback when selection is made */
  onSelectionMade?: () => void
  /** Whether this card is part of a multi-question set */
  isMultiQuestion?: boolean
  /** Index in multi-question set (for staggered animations) */
  questionIndex?: number
  /** Optional pager/actions rendered beside the question heading. */
  headerAccessory?: ReactNode
  /** Keep the full option-list presentation even when there is one option. */
  forceListLayout?: boolean
  /** Render compact numeric badges instead of radio circles. */
  showOptionNumbers?: boolean
  /**
   * Canvas nodes offered as `@mention` targets in the freeform answer
   * (Workplace post 1585245219899442 — parity with the canvas composer).
   *
   * OPT-IN on purpose. `QuestionCard` also renders on the landing
   * (`ScriptWriterDrawer`, `BatchedQuestionCard`) and on
   * `PublishedProjectPage`, where there is no canvas to mention. Omitting this
   * prop keeps the plain `<input>` those surfaces have always used, so mention
   * support cannot regress them.
   */
  canvasNodes?: ReactFlowCanvasNode[]
}

export const QuestionCard = memo(forwardRef<HTMLDivElement, QuestionCardProps>(function QuestionCard({
  question,
  options,
  nodeRef,
  byomConfiguration,
  selectedOption,
  onSelect,
  disabled = false,
  allowCustomInput = false,
  customText = '',
  customSelected = false,
  onCustomTextChange,
  onCustomInputFocus,
  onCustomSubmit,
  onSelectionMade,
  isMultiQuestion = false,
  questionIndex = 0,
  headerAccessory,
  forceListLayout = false,
  showOptionNumbers = false,
  canvasNodes,
}, ref) {
  const { t } = useTranslation('chat')
  const radioGroupName = useId()
  const [localSelected, setLocalSelected] = useState<string | undefined>(selectedOption)
  const [localCustomSelected, setLocalCustomSelected] = useState(
    customSelected || (!selectedOption && !!customText.trim()),
  )
  const composerRef = useRef<InlineMentionComposerHandle>(null)

  // The composer is UNCONTROLLED after mount: InlineMentionComposer hydrates
  // `initialContent` exactly once (initialContentHydratedRef), so a later
  // external change to `customText` does not reach the editor. The plain
  // <input> it replaces was controlled via `value`, so this asymmetry is a
  // regression introduced with mention support: choosing a preset option calls
  // setCustomTexts(id, '') in AgentChat, and without this the editor would keep
  // showing the old text and chips while submission — which reads customTexts —
  // sends nothing, leaving Enter dead.
  //
  // Only the *clear* direction is synced. Every keystroke round-trips through
  // onContentChange -> parent -> back here, so the values already agree while
  // typing; calling setText on that path would fight the caret.
  useEffect(() => {
    if (customText !== '') return
    const handle = composerRef.current
    if (!handle || handle.getPlainText() === '') return
    handle.clear()
  }, [customText])

  const isCustomSelected = localCustomSelected || customSelected
  const resolvedSelected = isCustomSelected ? undefined : (localSelected || selectedOption)
  const hasSelection = !!resolvedSelected || isCustomSelected

  // Passive identity chip: names WHICH clip/panel this card is about, so the
  // user does not have to scan the grid before deciding. Backend-resolved and
  // fail-closed — if there is no clean label we render nothing (never
  // "Clip undefined"). Non-interactive: the commit surface is the option button.
  let identityChipLabel = ''
  if (nodeRef) {
    const parts: string[] = []
    if (nodeRef.kind === 'clip' && typeof nodeRef.number === 'number') {
      parts.push(t('questionCard.clip', { number: nodeRef.number }))
    } else if (nodeRef.kind === 'panel' && typeof nodeRef.number === 'number') {
      parts.push(t('questionCard.panel', { number: nodeRef.number }))
    } else if (nodeRef.name) {
      parts.push(nodeRef.name)
    }
    if (typeof nodeRef.version === 'number') {
      parts.push(t('questionCard.version', { version: nodeRef.version }))
    }
    identityChipLabel = parts.join(t('questionCard.chipSep'))
  }
  const identityChip = identityChipLabel ? (
    <span
      data-testid="question-identity-chip"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border-subtle bg-surface-1 text-xs font-medium text-text-muted select-none"
    >
      {identityChipLabel}
    </span>
  ) : null

  useEffect(() => {
    setLocalSelected(selectedOption)
    if (selectedOption) setLocalCustomSelected(false)
  }, [selectedOption])

  useEffect(() => {
    if (customSelected || (!selectedOption && customText.trim())) {
      setLocalCustomSelected(true)
    }
  }, [customSelected, customText, selectedOption])

  const handleSelect = (value: string) => {
    if (disabled) return
    setLocalSelected(value)
    setLocalCustomSelected(false)
    onSelect(value)

    // In multi-question mode, notify parent to scroll to next question
    if (isMultiQuestion && !hasSelection) {
      setTimeout(() => {
        onSelectionMade?.()
      }, 150)
    }
  }

  const capabilityRows: ReadonlyArray<readonly [string, string, string[]]> = byomConfiguration
    ? [
        ['duration', t('questionCard.byom.duration'), byomConfiguration.capabilities.duration_range],
        ['aspect-ratios', t('questionCard.byom.aspectRatios'), byomConfiguration.capabilities.aspect_ratios],
        ['resolutions', t('questionCard.byom.resolutions'), byomConfiguration.capabilities.resolutions],
        ['audio-control', t('questionCard.byom.audioControl'), byomConfiguration.capabilities.audio_control],
        ['image-references', t('questionCard.byom.imageReferences'), byomConfiguration.capabilities.reference_images_support],
        ['video-references', t('questionCard.byom.videoReferences'), byomConfiguration.capabilities.reference_videos_support],
        ['audio-references', t('questionCard.byom.audioReferences'), byomConfiguration.capabilities.reference_audios_support],
        ['total-references', t('questionCard.byom.totalReferences'), byomConfiguration.capabilities.max_total_references],
      ]
    : []

  // Single option: inline text prompt
  if (options.length === 1 && !forceListLayout) {
    const option = options[0]
    const optionLabel = typeof option === 'string' ? option : option.label
    const optionValue = typeof option === 'object' ? (option.value || optionLabel) : optionLabel

    return (
      <div className="my-3 animate-fade-in-down">
        {identityChip && <div className="mb-1.5">{identityChip}</div>}
        <span className="text-base text-text-secondary">
          <MentionMarkupText text={question} surface="question" />{' '}
          <button
            onClick={() => handleSelect(optionValue)}
            disabled={disabled}
            className="text-text-primary underline underline-offset-2 hover:text-chrome transition-colors"
          >
            <MentionMarkupText text={optionLabel} surface="question_option" interactive={false} />
          </button>
        </span>
      </div>
    )
  }

  // Multiple options: staggered fade-in animation
  return (
    <div 
      ref={ref}
      data-testid="question-card" 
      className="my-4 transition-all duration-300"
    >
      {/* Question header - always visible, uses chat font style */}
      <div
        className={cn('mb-3 flex items-start justify-between gap-3', !disabled && 'animate-fade-in-down')}
        style={!disabled ? { animationDelay: isMultiQuestion ? `${questionIndex * 100}ms` : '300ms' } : undefined}
      >
        <div className="min-w-0 flex-1">
          {identityChip && <div className="mb-1.5">{identityChip}</div>}
          <span data-testid="question-header" className="text-base text-text-secondary">
            <MentionMarkupText text={question} surface="question" />
          </span>
        </div>
        {headerAccessory}
      </div>

      {byomConfiguration && (
        <div
          data-testid="byom-configuration-preview"
          className="mb-3 overflow-hidden rounded-lg border border-border bg-surface-1"
        >
          <div className="border-b border-border-subtle px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                {t('questionCard.byom.title')}
              </span>
              <span className="rounded-full border border-border px-2 py-0.5 text-xxs font-medium uppercase text-muted-foreground">
                {t(`questionCard.byom.${byomConfiguration.action}`)}
              </span>
            </div>
            <div className="mt-1 text-base font-medium text-foreground">
              {byomConfiguration.key_name}
            </div>
            {byomConfiguration.provider_model && (
              <code
                data-testid="byom-provider-model"
                className="mt-1 block break-all text-xs font-medium text-muted-foreground"
              >
                {byomConfiguration.provider_model}
              </code>
            )}
          </div>
          <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-2 px-3 py-2.5">
            {capabilityRows.map(([id, label, values]) => {
              return (
                <div key={id} className="contents">
                  <dt className="text-sm text-text-muted">{label}</dt>
                  <dd
                    data-testid={`byom-capability-${id}`}
                    className="break-words text-right text-sm font-medium text-foreground"
                  >
                    {values.length > 0 ? values.join(', ') : t('questionCard.byom.none')}
                  </dd>
                </div>
              )
            })}
          </dl>
        </div>
      )}

      {/* Options - staggered fade in from top */}
      <div className="flex flex-col gap-1.5" aria-label={question}>
        {options.map((option, index) => {
          const optionLabel = typeof option === 'string' ? option : option.label
          const optionValue = typeof option === 'object' ? (option.value || optionLabel) : optionLabel
          const optionDescription = typeof option === 'object' ? option.description : undefined
          const isSelected = resolvedSelected === optionValue
          const isOther = hasSelection && !isSelected

          return (
            <button
              key={index}
              type="button"
              data-testid={`question-option-${optionValue}`}
              onClick={() => handleSelect(optionValue)}
              disabled={disabled}
              aria-pressed={isSelected}
              className={cn(
                'w-full px-3 py-2 text-left transition-all duration-300',
                'focus:outline-none focus-visible:border-chrome focus-visible:ring-2 focus-visible:ring-chrome/20 rounded border',
                !disabled && 'animate-fade-in-down',
                disabled && 'cursor-not-allowed',
                isSelected && !disabled && 'bg-surface-1 border-chrome',
                isSelected && disabled && 'bg-surface-1/50 border-chrome/60',
                !isSelected && !disabled && 'border-input-border bg-transparent hover:border-input-border-hover hover:bg-surface-2 cursor-pointer',
                !isSelected && disabled && 'border-border-subtle bg-transparent'
              )}
              style={!disabled ? {
                animationDelay: isMultiQuestion
                  ? `${(questionIndex * 100) + 100 + (index * 50)}ms`
                  : `${400 + (index * 75)}ms`,
              } : undefined}
            >
              <div className="flex items-start gap-2">
                <span
                  data-testid="question-option-indicator"
                  data-selected={isSelected}
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 inline-flex flex-shrink-0 items-center justify-center border font-medium',
                    showOptionNumbers ? 'h-5 w-5 rounded text-xxs' : 'h-3.5 w-3.5 rounded-full border-2',
                    isSelected && showOptionNumbers && 'border-chrome bg-chrome text-background',
                    isSelected && !showOptionNumbers && 'border-chrome bg-chrome',
                    !isSelected && showOptionNumbers && 'border-input-border bg-surface-2 text-muted-foreground',
                    !isSelected && !showOptionNumbers && 'border-input-border bg-transparent',
                  )}
                >
                  {showOptionNumbers ? index + 1 : null}
                </span>
                <div className="min-w-0">
                  <div className={cn(
                    'text-base font-medium',
                    isOther ? 'text-text-muted' : 'text-text-primary'
                  )}>
                    <MentionMarkupText text={optionLabel} surface="question_option" interactive={false} />
                  </div>
                  {optionDescription && (
                    <div className={cn(
                      'text-sm leading-snug whitespace-pre-line',
                      isOther ? 'text-text-muted/60' : 'text-text-muted',
                    )}>
                      <MentionMarkupText text={optionDescription} surface="question_option" interactive={false} />
                    </div>
                  )}
                </div>
              </div>
            </button>
          )
        })}

        {/* Custom input is a first-class radio option, including read-only history. */}
        {allowCustomInput && (
          <div
            data-testid="question-custom-option"
            onClick={(event) => {
              if (disabled) return
              const field = event.currentTarget.querySelector<HTMLElement>(ANSWER_FIELD_SELECTOR)
              if (event.target !== field) field?.focus()
            }}
            className={cn(
              'mt-0.5 flex items-center gap-2 px-3 py-2 rounded border bg-input-bg transition-all duration-300',
              !disabled && 'animate-fade-in-down hover:bg-surface-3 hover:border-input-border-hover focus-within:border-chrome focus-within:ring-2 focus-within:ring-chrome/20',
              isCustomSelected && 'border-chrome ring-1 ring-chrome/20',
              !isCustomSelected && 'border-input-border',
              disabled && !isCustomSelected && 'border-border-subtle',
            )}
            style={!disabled ? { animationDelay: `${400 + (options.length * 75)}ms` } : undefined}
          >
            <input
              type="radio"
              name={radioGroupName}
              checked={isCustomSelected}
              onChange={(event) => {
                if (disabled) return
                setLocalSelected(undefined)
                setLocalCustomSelected(true)
                event.currentTarget.parentElement
                  ?.querySelector<HTMLElement>(ANSWER_FIELD_SELECTOR)
                  ?.focus()
              }}
              disabled={disabled}
              aria-label={t('questionCard.customPlaceholder')}
              data-testid="question-custom-indicator"
              data-selected={isCustomSelected}
              className={cn(
                'appearance-none inline-block w-3.5 h-3.5 rounded-full border-2 flex-shrink-0',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-chrome/20',
                isCustomSelected
                  ? 'border-chrome bg-chrome'
                  : 'border-input-border bg-transparent',
              )}
            />
            {canvasNodes ? (
              /* Mention-capable freeform answer (post 1585245219899442). The
                 composer owns Enter-to-submit and Escape internally, and
                 serialises chips to `@[[nodeId]]` markers inside `plainText`
                 — the same encoding the canvas composer sends, which the
                 backend resolves into node context on the answer path.
                 `initialContent` rehydrates persisted markers back into chips
                 (#3019 persistence + #3020 chip rendering). */
              <div className="min-w-0 flex-1" data-testid="question-custom-mention-composer">
                <InlineMentionComposer
                  ref={composerRef}
                  canvasNodes={canvasNodes}
                  disabled={disabled}
                  placeholder={t('questionCard.customPlaceholder')}
                  initialContent={customText}
                  onContentChange={({ plainText }) => {
                    if (disabled) return
                    setLocalSelected(undefined)
                    setLocalCustomSelected(!!plainText.trim())
                    onCustomTextChange?.(plainText)
                  }}
                  onFocusChange={(isFocused) => {
                    if (disabled || !isFocused) return
                    setLocalSelected(undefined)
                    setLocalCustomSelected(true)
                    questionCardLog({
                      event: 'question-card:custom_option_selected',
                      selectionSource: 'focus',
                      customTextLength: customText.length,
                    })
                    onCustomInputFocus?.()
                  }}
                  onSubmit={() => {
                    if (!customText.trim() || !onCustomSubmit) return
                    questionCardLog({
                      event: 'question-card:custom_submit_requested',
                      customTextLength: customText.trim().length,
                      hasMentions: customText.includes('@[['),
                    })
                    onCustomSubmit()
                  }}
                />
              </div>
            ) : (
              <input
                type="text"
                value={customText}
                onChange={(e) => {
                  if (!disabled) {
                    setLocalSelected(undefined)
                    setLocalCustomSelected(!!e.target.value.trim())
                    onCustomTextChange?.(e.target.value)
                  }
                }}
                onFocus={() => {
                  if (disabled) return
                  setLocalSelected(undefined)
                  setLocalCustomSelected(true)
                  questionCardLog({
                    event: 'question-card:custom_option_selected',
                    selectionSource: 'focus',
                    customTextLength: customText.length,
                  })
                  onCustomInputFocus?.()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customText.trim() && onCustomSubmit) {
                    e.preventDefault()
                    questionCardLog({
                      event: 'question-card:custom_submit_requested',
                      customTextLength: customText.trim().length,
                    })
                    onCustomSubmit()
                  }
                }}
                placeholder={t('questionCard.customPlaceholder')}
                disabled={disabled}
                className={cn(
                  'min-w-0 flex-1 bg-input-bg text-base text-foreground placeholder:text-text-muted',
                  'focus:outline-none disabled:cursor-not-allowed',
                )}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}))
